import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { getCurrentUser, getToken } from "@/lib/utils";
import {
  AUTH_ROUTE_PATHS,
  JOURNEY_STEP_KEYS,
  getJourneyPath,
} from "@/pages/Auth/authConfig";
import { getSignUpJourneyDraft } from "@/pages/Auth/signUpJourney";
import { isApproved, isBlockedStatus } from "@/types/userStatus";

interface ConditionalRouteProps {
  component: React.ComponentType;
  isPrivate?: boolean;
}

const isOnboardingPath = (pathname: string) =>
  /^\/(welcome|questions|introduction)(\/|$)/.test(pathname);

const getIncompleteOnboardingDraft = () => {
  const draft = getSignUpJourneyDraft();
  if (!draft?.accountCreated || draft.questionsCompleted) {
    return null;
  }
  return draft;
};

// Returns the redirect path for a user who should NOT access the current route,
// or null if the user is APPROVED and may proceed.
const getStatusRedirect = (status: string | null | undefined): string | null => {
  if (isBlockedStatus(status)) return AUTH_ROUTE_PATHS.rejected;
  if (isApproved(status)) return null;
  // UNDER_REVIEW, null, or any unrecognised status → waitlist
  return AUTH_ROUTE_PATHS.waitlist;
};

const ConditionalRoute: React.FC<ConditionalRouteProps> = ({
  component: Component,
  isPrivate = false,
}) => {
  const isAuthenticated = !!getToken();
  const location = useLocation();
  const onboardingPath = isOnboardingPath(location.pathname);
  const incompleteOnboardingDraft = getIncompleteOnboardingDraft();

  // Signup stays public so Back navigation from Welcome still works.
  const isAuthenticatedPublicJourneyPath =
    onboardingPath || /^\/signup(\/|$)/.test(location.pathname);

  if (isPrivate && !isAuthenticated) {
    return <Navigate replace to={AUTH_ROUTE_PATHS.getStarted} />;
  }

  if (
    isAuthenticated &&
    incompleteOnboardingDraft &&
    !onboardingPath &&
    !/^\/signup(\/|$)/.test(location.pathname)
  ) {
    return (
      <Navigate
        replace
        to={getJourneyPath(
          JOURNEY_STEP_KEYS.welcome,
          incompleteOnboardingDraft.role,
        )}
      />
    );
  }

  if (isPrivate && isAuthenticated && !onboardingPath) {
    const statusRedirect = getStatusRedirect(getCurrentUser("status"));
    if (statusRedirect) return <Navigate replace to={statusRedirect} />;
  }

  if (!isPrivate && isAuthenticated && !isAuthenticatedPublicJourneyPath) {
    const statusRedirect = getStatusRedirect(getCurrentUser("status"));
    return <Navigate replace to={statusRedirect ?? "/home"} />;
  }

  return <Component />;
};

export default ConditionalRoute;
